import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Alert } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaMapMarkerAlt, FaTags, FaEdit, FaTrash } from 'react-icons/fa';
import { API_ENDPOINTS, getImageUrl } from '../config/api';
import BackToHome from '../components/BackToHome';
import './MyAds.css';

const MyAds = () => {
  const [ads, setAds] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMyAds = async () => {
      if (!currentUser) {
        setError('Please log in to view your ads');
        setLoading(false);
        return;
      }

      try {
        const token = await currentUser.getIdToken();
        const response = await axios.get(API_ENDPOINTS.ADS.MY_ADS, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        setAds(response.data);
        setError(null);
      } catch (error) {
        console.error('Error fetching my ads:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        console.error('Error headers:', error.response?.headers);
        setError(error.response?.data?.error || 'Failed to fetch your ads');
        toast.error(error.response?.data?.error || 'Failed to fetch your ads');
      } finally {
        setLoading(false);
      }
    };

    fetchMyAds();
  }, [currentUser]);

  const handleDeleteAd = async (adId) => {
    try {
      const token = await currentUser.getIdToken();
      await axios.delete(API_ENDPOINTS.ADS.DELETE(adId), {
        headers: { 
          Authorization: `Bearer ${token}` 
        }
      });
      setAds(ads.filter(ad => ad._id !== adId));
      toast.success('Ad deleted successfully');
    } catch (error) {
      console.error('Error deleting ad:', error);
      setError('Failed to delete ad');
      toast.error('Failed to delete ad');
    }
  };

  const handleEditAd = (adId) => {
    navigate(`/edit-ad/${adId}`);
  };

  if (loading) {
    return (
      <Container>
        <BackToHome />
        <div className="text-center my-5">
          <p>Loading your ads...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <BackToHome />
        <Alert variant="danger" className="my-4">
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container>
      <BackToHome />
      <h2 className="my-4">My Ads</h2>
      {ads.length === 0 ? (
        <Alert variant="info">
          You have no ads posted yet. <Button variant="link" onClick={() => navigate('/post-ad')}>Post your first ad</Button>
        </Alert>
      ) : (
        <Row>
          {ads.map(ad => {
            const imageUrl = ad.images && ad.images.length > 0
              ? ad.images[0].startsWith('http')
                ? ad.images[0]
                : getImageUrl(ad.images[0])
              : 'https://via.placeholder.com/220x220';
            return (
              <Col key={ad._id} md={4} className="mb-4">
                <div className="my-ad-card">
                  <div className="my-ad-card-image-wrapper">
                    <img src={imageUrl} alt={ad.title} className="my-ad-card-img" />
                    <div className="my-ad-card-actions">
                      <Button 
                        variant="primary" 
                        size="sm"
                        className="my-ad-action-btn edit-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditAd(ad._id);
                        }}
                        title="Edit Ad"
                      >
                        <FaEdit />
                      </Button>
                      <Button 
                        variant="danger" 
                        size="sm"
                        className="my-ad-action-btn delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteAd(ad._id);
                        }}
                        title="Delete Ad"
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </div>
                  <div className="my-ad-card-content" onClick={() => navigate(`/ad/${ad._id}`)}>
                    <h3 className="my-ad-card-title">{ad.title}</h3>
                    <p className="my-ad-price">₹{ad.price?.toLocaleString()}</p>
                    <p className="my-ad-location">
                      <FaMapMarkerAlt /> {ad.city ? `${ad.city}, ${ad.state || ''}` : ad.location || 'Location not specified'}
                    </p>
                    <p className="my-ad-category">
                      <FaTags /> {ad.category || 'Uncategorized'}
                    </p>
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      )}
    </Container>
  );
};

export default MyAds;
